import DeviceProps from '@magnus/react-native-device-props';

import { instance } from '../api/interceptor';

/**
 * Truegate deferred-email flow: claim the purchase(s) made as an anonymous
 * customer for the email the user gave on the `email-after-payment` page.
 *
 * Truegate's counterpart to Stripe's POST /auth/email/attach, which stays
 * Stripe-only: there is no guest bearer in the truegate flow, so the purchase is
 * identified by its transaction id(s) instead and the call needs no auth.
 *
 * The synthetic `anon-<uuid>@voery.app` address the truegate customer was created
 * with must never be passed as `email` — the endpoint rejects placeholders with a
 * 400.
 */

export type ClaimOutcome =
  /** Email linked, purchases replayed onto the account. */
  | { status: 'linked'; replayed?: number; failed?: number }
  /** Email belongs to another account; ownership must be verified over OTP. */
  | { status: 'requires-otp' }
  /** 409 — this payment is already linked to a different email. */
  | { status: 'already-claimed' }
  /** 400 — placeholder/invalid email, or no transaction ids to claim. */
  | { status: 'invalid' }
  /** Network failure or unexpected response. */
  | { status: 'error' };

export async function claimTruegatePurchase({
  email,
  transactionIds,
}: {
  email: string;
  transactionIds: string[];
}): Promise<ClaimOutcome> {
  if (!transactionIds.length) {
    // Nothing to claim — the purchase transaction never reached the store.
    return { status: 'invalid' };
  }

  // idfm — lets the backend match the purchase to this device as well.
  let externalUserId: string | undefined;
  try {
    externalUserId = await DeviceProps.getIDFM();
  } catch (error) {
    console.warn('[claimTruegatePurchase] could not read idfm', error);
  }

  try {
    // Sent through the shared Voery API client so the request carries the
    // project's `x-api-key`. No Authorization header — the endpoint needs none.
    const { data } = await instance.post('/truegate/claim', {
      email,
      transactionIds,
      ...(externalUserId && { externalUserId }),
    });

    if (data?.requiresOtp) {
      return { status: 'requires-otp' };
    }

    if (data?.linked) {
      return { status: 'linked', replayed: data.replayed, failed: data.failed };
    }

    console.error('[claimTruegatePurchase] unexpected response', data);
    return { status: 'error' };
  } catch (error: any) {
    const httpStatus = error?.response?.status;

    if (httpStatus === 409) {
      return { status: 'already-claimed' };
    }

    if (httpStatus === 400) {
      return { status: 'invalid' };
    }

    console.error('[claimTruegatePurchase] request failed', error);
    return { status: 'error' };
  }
}
